/**
 * LoginRedirect - Backwards-compatible redirect for /login route
 *
 * Issue #1329: Consolidate login into modal-only flow.
 *
 * Handles:
 * - Direct URL access to /login
 * - Bookmarks to /login
 * - External links (marketing emails, docs)
 *
 * Behavior:
 * - If authenticated: redirect to /home
 * - If not authenticated: redirect to / and open login modal
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../contexts/AuthModalContext';

export default function LoginRedirect() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { openLoginModal } = useAuthModal();

  useEffect(() => {
    // Wait for auth check to complete
    if (isLoading) return;

    if (isAuthenticated) {
      // Already logged in, go home
      navigate('/home', { replace: true });
    } else {
      // Go to landing page and open modal
      navigate('/', { replace: true });
      // Small delay to ensure navigation completes before opening modal
      const timer = setTimeout(() => openLoginModal('/home'), 50);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading, navigate, openLoginModal]);

  // Show nothing while redirecting
  return null;
}
